import create from 'zustand';
import { lookUpService } from '../../Services/common/lookup.service';
import { ApiResponse } from '../../model/common/apiResponse';
import { BehaviorSubject, Observable, of } from 'rxjs';

const getClientFromStorage = () => {
  try {
    const encrypted_client = localStorage.getItem("client");
    return encrypted_client ? JSON.parse(atob(encrypted_client)) : null;
  } catch (error) {
    console.error("Failed to parse client from localStorage", error);
    return null;
  }
};

const initialClient = getClientFromStorage();
const clientSubject = new BehaviorSubject<Client | null>(initialClient);

export type Client = {
  id: number;
  code: string;
  name: string;
};

interface ClientStore {
  clients: Client[];
  loading: boolean;
  error: string | null;
  fetchClient: (skip_store?: boolean) => Promise<void>;
  onchangeClient: (client: Client | null) => void;
  selectedClient: Client | null;
  getSelectedClient: () => Observable<Client | null>;
  reset: () => void;
}

const useClientStore = create<ClientStore>((set, get) => {
  const fetchClient = async (skip_store: boolean = false) => {
    const state = get(); 
    if (state?.clients.length > 0 && !skip_store) return;
    set({ loading: true, error: null });
    try {
      const response = await lookUpService.clientLookup(true);
      const data = response.data as ApiResponse<any>;
      set({ clients: data?.data?.results, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  };

  const onchangeClient = (client: Client | null) => {
    try {
      if (client) {
        localStorage.setItem('client', btoa(JSON.stringify(client)));
      } else {
        localStorage.removeItem('client');
      }
      set({ selectedClient: client });
      clientSubject.next(client);
    } catch (error) {
      console.error("Failed to update client", error);
    }
  };

  const getSelectedClient = (): Observable<Client | null> => {
    return clientSubject.asObservable();
  };

  const reset = () => {
    try {
      localStorage.removeItem('client');
      set({ 
        clients: [], 
        loading: false, 
        error: null, 
        selectedClient: null 
      });
      clientSubject.next(null);
    } catch (error) {
      console.error("Failed to reset client store", error);
    }
  };

  return {
    clients: [], 
    loading: true,
    error: null,
    selectedClient: initialClient,
    fetchClient,
    onchangeClient,
    getSelectedClient,
    reset
  };
});

export default useClientStore;